import { Alert, Button, FormField, Stack, Text } from '@ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { formErrors } from '../../../lib/errors';
import { useResetPassword } from '../api';
import { PasswordInput } from '../components/PasswordInput';
import { PublicPage } from '../components/PublicPage';

/** Sets a new password with the link from the e-mail (AUTH-004). */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const reset = useResetPassword();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (password !== repeat) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    reset.mutate({ token, password });
  };

  if (!token) {
    return (
      <PublicPage title={t('auth.reset.title')}>
        <Alert tone="danger">{t('auth.reset.missingToken')}</Alert>
        <Text size="sm">
          <Link to="/passwort-vergessen">{t('auth.forgot.title')}</Link>
        </Text>
      </PublicPage>
    );
  }

  if (reset.isSuccess) {
    return (
      <PublicPage title={t('auth.reset.title')}>
        <Alert tone="success">{t('auth.reset.done')}</Alert>
        <Text size="sm">
          <Link to="/anmelden">{t('auth.reset.toLogin')}</Link>
        </Text>
      </PublicPage>
    );
  }

  const errors = formErrors(reset.error, { password: ['password.'] });
  return (
    <PublicPage title={t('auth.reset.title')}>
      <Stack as="form" gap={4} onSubmit={onSubmit} noValidate>
        {errors.general && (
          <Alert
            tone="danger"
            actions={<Link to="/passwort-vergessen">{t('auth.forgot.title')}</Link>}
          >
            {errors.general}
          </Alert>
        )}
        <FormField
          label={t('auth.reset.newPassword')}
          hint={t('auth.passwordHint')}
          error={errors.field.password}
          required
        >
          {(p) => (
            <PasswordInput
              {...p}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </FormField>
        <FormField
          label={t('auth.reset.repeat')}
          error={mismatch ? t('auth.reset.mismatch') : undefined}
          required
        >
          {(p) => (
            <PasswordInput
              {...p}
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
          )}
        </FormField>
        <Button type="submit" size="lg" fullWidth loading={reset.isPending}>
          {t('auth.reset.submit')}
        </Button>
      </Stack>
    </PublicPage>
  );
}
